import type { TaskColumnId } from './types'

export const ORDERING_VALUES = [
  'manual',
  'priority',
  'dueDate',
  'createdDate',
  'title',
] as const

export type OrderingKey = (typeof ORDERING_VALUES)[number]

export const ORDERING_OPTIONS: Array<{ value: OrderingKey; label: string }> = [
  { value: 'manual', label: 'Manual' },
  { value: 'priority', label: 'Priority' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'createdDate', label: 'Created date' },
  { value: 'title', label: 'Title' },
]

export const PRIORITY_VALUES = ['HIGH', 'MEDIUM', 'LOW'] as const

export type PriorityKey = (typeof PRIORITY_VALUES)[number]

export const PRIORITY_OPTIONS: Array<{ value: PriorityKey; label: string }> = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
]

export type CardPropertyToggles = {
  priority: boolean
  description: boolean
  assignee: boolean
  dueDate: boolean
}

export const DEFAULT_CARD_PROPERTIES: CardPropertyToggles = {
  priority: true,
  description: true,
  assignee: true,
  dueDate: true,
}

export const VIEW_MODE_VALUES = ['comfortable', 'compact'] as const

export type ViewMode = (typeof VIEW_MODE_VALUES)[number]

export const CATEGORY_VALUES = [
  'evidence',
  'remediation',
  // #1418 — filter FACETS over evidence cards, not separate card sources:
  // "Agent escalations" = tests with an active escalation in their CES lane;
  // "Policy approvals" = POLICY tests whose policy awaits approval.
  'agent_escalation',
  'policy_approval',
  'consistency_gap',
  'scan_finding',
  // RFC-206 §7.1 — system-fired procedure runs needing attention
  // (waiting on a human step / failed). Read-only projections.
  'procedure_run',
  'manual',
] as const

export type TaskCategory = (typeof CATEGORY_VALUES)[number]

// The board defaults to human-driven work (Tests + Tasks + the low-volume
// attention categories). Auto-generated scanner output is hidden by default so
// it doesn't flood the board — it stays one click away via the category filter
// and keeps its own homes:
//   - `remediation` (REM-…): cloud/repo-scan + gap-detector findings; reachable
//     from each test's Findings section (CES-linked) or its own detail page.
//   - `scan_finding` (SCN-…): read-only, pre-triage raw scanner output;
//     reachable from the scan / integrations / analysis surfaces.
const AUTO_GENERATED_CATEGORIES: ReadonlySet<TaskCategory> = new Set([
  'remediation',
  'scan_finding',
])

export const DEFAULT_CATEGORY_VALUES = CATEGORY_VALUES.filter(
  (category) => !AUTO_GENERATED_CATEGORIES.has(category)
)

// True when the category filter is untouched (equals the default selection).
// Drives the "active filter" indicator so the default-hidden `remediation`
// category doesn't make the board look permanently filtered.
export function isDefaultCategorySelection(
  selected: ReadonlySet<TaskCategory>
): boolean {
  return (
    selected.size === DEFAULT_CATEGORY_VALUES.length &&
    DEFAULT_CATEGORY_VALUES.every((category) => selected.has(category))
  )
}

export const CATEGORY_OPTIONS: Array<{
  value: TaskCategory
  label: string
}> = [
  { value: 'evidence', label: 'Tests' },
  { value: 'remediation', label: 'Remediation' },
  { value: 'agent_escalation', label: 'Agent escalations' },
  { value: 'policy_approval', label: 'Policy approvals' },
  { value: 'consistency_gap', label: 'Consistency gaps' },
  { value: 'scan_finding', label: 'Scan findings' },
  { value: 'procedure_run', label: 'Procedure runs' },
  { value: 'manual', label: 'Manual tasks' },
]

// #1418 — an evidence card matches a facet when it carries that state.
// Mirrors the server's facet interpretation in buildActionItems.
export function evidenceCardMatchesCategory(
  item: { category: string; meta?: Record<string, unknown> },
  selected: ReadonlySet<TaskCategory>
): boolean {
  if (selected.has('evidence')) return true
  if (
    selected.has('agent_escalation') &&
    typeof item.meta?.agentEscalationReason === 'string' &&
    item.meta.agentEscalationReason
  ) {
    return true
  }
  if (
    selected.has('policy_approval') &&
    item.meta?.awaitingPolicyApproval === true
  ) {
    return true
  }
  return false
}

export const ALL_STATUS_IDS = [
  'backlog',
  'todo',
  'in_progress',
  'in_review',
  'done',
] as const satisfies readonly TaskColumnId[]

export const ALL_PRIORITIES = PRIORITY_VALUES
